import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Original function for backward compatibility (manual single fetch)
export const fetchTechnicalIndicators = createAsyncThunk(
    'forex/fetchTechnicalIndicators',
    async ({ timeframe, symbol, apiKey }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`/api/forex/technical-indicators`, {
                params: {
                    timeframe,
                    symbol,
                    apiKey
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message });
        }
    }
);

// Main function to get forex data by symbol (fast from Redis)
export const getCachedTechnicalIndicators = createAsyncThunk(
    'forex/getCachedTechnicalIndicators',
    async ({ symbol, timeframe = 'daily', apiKey }, { rejectWithValue }) => {
        try {
            const params = {};
            if (symbol) params.symbol = symbol;
            if (timeframe) params.timeframe = timeframe;
            if (apiKey) params.apiKey = apiKey;
            
            const response = await axios.get(`/api/forex/get-technical-indicators`, {
                params
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message });
        }
    }
);

// Get available symbols
export const getAvailableSymbols = createAsyncThunk(
    'forex/getAvailableSymbols',
    async ({ apiKey }, { rejectWithValue }) => {
        try {
            const params = {};
            if (apiKey) params.apiKey = apiKey;
            
            const response = await axios.get(`/api/forex/symbols`, {
                params
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message });
        }
    }
);

// Control functions (admin only - still use JWT)
export const startDataFetching = createAsyncThunk(
    'forex/startDataFetching',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`/api/forex/start-fetching`, {}, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message });
        }
    }
);

const initialState = {
    technicalIndicators: null,
    availableSymbols: [],
    selectedSymbol: 'USDINR',
    selectedTimeframe: 'daily',
    apiKey: '', // Add API key to state
    loading: false,
    error: null,
    responseTime: null,
    dataAge: null,
    source: null,
    userInfo: null // Add user info from API response
};

const forexSlice = createSlice({
    name: 'forex',
    initialState,
    reducers: {
        setSelectedSymbol: (state, action) => {
            state.selectedSymbol = action.payload;
        },
        setSelectedTimeframe: (state, action) => {
            state.selectedTimeframe = action.payload;
        },
        setApiKey: (state, action) => {
            state.apiKey = action.payload;
            // Save to localStorage for persistence
            if (action.payload) {
                localStorage.setItem('forexApiKey', action.payload);
            } else {
                localStorage.removeItem('forexApiKey');
            }
        },
        loadApiKeyFromStorage: (state) => {
            const savedApiKey = localStorage.getItem('forexApiKey');
            if (savedApiKey) {
                state.apiKey = savedApiKey;
            }
        },
        clearError: (state) => {
            state.error = null;
        },
        resetForexState: (state) => {
            return { 
                ...initialState, 
                availableSymbols: state.availableSymbols,
                apiKey: state.apiKey // Preserve API key on reset
            };
        }
    },
    extraReducers: (builder) => {
        builder
            // Handle fetchTechnicalIndicators (manual single fetch)
            .addCase(fetchTechnicalIndicators.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTechnicalIndicators.fulfilled, (state, action) => {
                state.loading = false;
                state.technicalIndicators = action.payload.data;
                state.source = action.payload.source;
                state.userInfo = action.payload.user_info;
                state.error = null;
            })
            .addCase(fetchTechnicalIndicators.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || action.payload?.details || 'Failed to fetch technical indicators';
            })
            
            // Handle getCachedTechnicalIndicators (fast Redis lookup)
            .addCase(getCachedTechnicalIndicators.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCachedTechnicalIndicators.fulfilled, (state, action) => {
                state.loading = false;
                state.technicalIndicators = action.payload.data;
                state.responseTime = action.payload.response_time_ms;
                state.dataAge = action.payload.data_age_seconds;
                state.source = action.payload.source;
                state.userInfo = action.payload.user_info;
                state.error = null;
            })
            .addCase(getCachedTechnicalIndicators.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || action.payload?.details || 'Failed to fetch cached indicators';
            })
            
            // Handle getAvailableSymbols
            .addCase(getAvailableSymbols.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAvailableSymbols.fulfilled, (state, action) => {
                state.loading = false;
                state.availableSymbols = action.payload.symbols;
                state.error = null;
            })
            .addCase(getAvailableSymbols.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || action.payload?.details || 'Failed to fetch available symbols';
            })
            
            // Handle startDataFetching
            .addCase(startDataFetching.fulfilled, (state, action) => {
                console.log('Data fetching started:', action.payload.message);
            });
    }
});

export const { 
    setSelectedSymbol, 
    setSelectedTimeframe, 
    setApiKey,
    loadApiKeyFromStorage,
    clearError, 
    resetForexState 
} = forexSlice.actions;

// Selectors
export const selectTechnicalIndicators = (state) => state.forex.technicalIndicators;
export const selectAvailableSymbols = (state) => state.forex.availableSymbols;
export const selectSelectedSymbol = (state) => state.forex.selectedSymbol;
export const selectSelectedTimeframe = (state) => state.forex.selectedTimeframe;
export const selectApiKey = (state) => state.forex.apiKey;
export const selectForexLoading = (state) => state.forex.loading;
export const selectForexError = (state) => state.forex.error;
export const selectResponseTime = (state) => state.forex.responseTime;
export const selectDataAge = (state) => state.forex.dataAge;
export const selectDataSource = (state) => state.forex.source;
export const selectUserInfo = (state) => state.forex.userInfo;

export default forexSlice.reducer;
